/* AIConnectionEngineCard — открива корелации между храна / спорт / психика
 * (iter421.4). Извиква /api/health-intelligence/connections и рендерира
 * top 5 връзки с patient-friendly наративи.
 *
 * Тон: винаги "област за подобрение" / "заслужава проследяване".
 */
import React, { useCallback, useEffect, useState } from "react";
import { useAuth } from "@/App";
import { useLanguage } from "@/i18n/LanguageContext";
import { useTheme } from "@/theme/ThemeContext";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import {
  Sparkles, TrendingUp, TrendingDown, Activity, RefreshCw, Loader2, Info,
} from "lucide-react";
import axios from "axios";

const STRENGTH_COLOR = {
  strong:   "from-rose-500 to-pink-500",
  moderate: "from-amber-500 to-orange-500",
  weak:     "from-slate-500 to-slate-600",
};

const STRENGTH_LABEL_BG = { strong: "силна", moderate: "умерена", weak: "слаба" };
const STRENGTH_LABEL_EN = { strong: "strong", moderate: "moderate", weak: "weak" };

export default function AIConnectionEngineCard() {
  const { API } = useAuth();
  const { language } = useLanguage();
  const { isDark } = useTheme();
  const bg = language === "bg";

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetch = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/health-intelligence/connections?days=45`);
      setData(r.data);
    } catch (_e) {
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [API]);

  useEffect(() => { fetch(); }, [fetch]);

  if (loading) {
    return (
      <Card className={`border ${isDark ? "border-slate-800 bg-slate-900/40" : "border-slate-200 bg-white"}`}
            data-testid="ai-connection-engine-loading">
        <CardContent className="p-6 flex items-center justify-center gap-2">
          <Loader2 className={`w-5 h-5 animate-spin ${isDark ? "text-violet-400" : "text-violet-600"}`} />
          <span className={`text-xs ${isDark ? "text-slate-400" : "text-slate-500"}`}>
            {bg ? "AI Connection Engine анализира…" : "AI Connection Engine analyzing…"}
          </span>
        </CardContent>
      </Card>
    );
  }
  if (!data) return null;

  const connections = data.connections || [];
  const nDays = data.n_days_with_data ?? 0;

  return (
    <Card className={`border ${isDark ? "border-violet-500/30 bg-gradient-to-br from-violet-500/5 to-fuchsia-500/5" : "border-violet-200 bg-gradient-to-br from-violet-50/60 to-fuchsia-50/40"}`}
          data-testid="ai-connection-engine">
      <CardContent className="p-4 space-y-3">
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 rounded-2xl flex items-center justify-center bg-gradient-to-br from-violet-500 to-fuchsia-500 text-white flex-shrink-0">
            <Sparkles className="w-5 h-5" />
          </div>
          <div className="flex-1">
            <h3 className={`text-sm font-bold ${isDark ? "text-white" : "text-slate-900"}`}>
              {bg ? "AI Connection Engine" : "AI Connection Engine"}
            </h3>
            <p className={`text-[11px] ${isDark ? "text-slate-400" : "text-slate-600"}`}>
              {bg
                ? `Открива връзки между храна, спорт и психика. Анализирани ${nDays} дни.`
                : `Discovers links between food, sport and psych. Analyzed ${nDays} days.`}
            </p>
          </div>
          <Button onClick={fetch} variant="ghost" size="sm" className="h-7 px-2" data-testid="ai-connection-refresh">
            <RefreshCw className="w-3.5 h-3.5" />
          </Button>
        </div>

        {connections.length === 0 ? (
          <div className={`p-4 rounded-xl border-dashed border-2 ${isDark ? "border-violet-500/30 bg-violet-500/5" : "border-violet-200 bg-white"}`}
               data-testid="ai-connection-empty">
            <p className={`text-xs leading-relaxed ${isDark ? "text-violet-200/80" : "text-violet-900/80"}`}>
              {bg
                ? "Все още няма достатъчно данни. За да активираш Connection Engine, лог-вай поне 7 дни сън/настроение/стрес ИЛИ спорт + поне 7 анализа на чиния. Колкото повече данни → толкова по-точни връзки."
                : "Not enough data yet. To activate Connection Engine, log at least 7 days of sleep/mood/stress OR sport + at least 7 plate analyses. More data → more accurate links."}
            </p>
          </div>
        ) : (
          <div className="space-y-2.5" data-testid="ai-connection-list">
            {connections.map((c, i) => {
              const Icon = c.direction === "positive" ? TrendingUp : TrendingDown;
              const color = STRENGTH_COLOR[c.strength] || STRENGTH_COLOR.weak;
              return (
                <div
                  key={i}
                  data-testid={`ai-connection-${i}`}
                  className={`p-3 rounded-xl border flex items-start gap-3 ${
                    isDark ? "border-slate-800 bg-slate-900/40" : "border-slate-200 bg-white"
                  }`}
                >
                  <div className={`w-9 h-9 rounded-xl flex-shrink-0 flex items-center justify-center bg-gradient-to-br ${color} text-white`}>
                    <Icon className="w-4 h-4" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center flex-wrap gap-2 mb-1">
                      <span className={`text-[10.5px] uppercase tracking-wider font-bold ${isDark ? "text-slate-400" : "text-slate-500"}`}>
                        {(bg ? c.x_label_bg : c.x_metric)} ↔ {(bg ? c.y_label_bg : c.y_metric)}
                      </span>
                      <span className={`text-[9.5px] px-1.5 py-0.5 rounded-full font-bold ${
                            isDark ? "bg-slate-800 text-slate-300" : "bg-slate-100 text-slate-600"
                          }`}>
                        r = {c.pearson_r} · n = {c.n_samples}
                      </span>
                      <span className={`text-[9.5px] px-1.5 py-0.5 rounded-full font-bold bg-gradient-to-r ${color} text-white`}>
                        {bg ? STRENGTH_LABEL_BG[c.strength] : STRENGTH_LABEL_EN[c.strength]}
                      </span>
                    </div>
                    <p className={`text-[12px] leading-relaxed ${isDark ? "text-slate-200" : "text-slate-800"}`}>
                      {bg ? c.narrative_bg : c.narrative_en}
                    </p>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        <p className={`text-[10px] italic flex items-start gap-1 ${isDark ? "text-slate-500" : "text-slate-500"}`}>
          <Info className="w-3 h-3 flex-shrink-0 mt-0.5" />
          <span>{bg ? `${data.method_bg} ${data.disclaimer_bg}` : `${data.method_en} ${data.disclaimer_en}`}</span>
        </p>
      </CardContent>
    </Card>
  );
}
